import { useAwsUrl } from "../../../context/Dashboard/aws/contextUrl.js";
import { useDesign } from "../../../context/contextDesign.js";

const ScanCard = () => {
  const {
    urlScanLoading,
    urlDashboardView,
    handleSubmit,
    setUrl,
    url,
    error
  } = useAwsUrl();
  const { toggleUpgradeCard } = useDesign();
  const urlExamples = [
    { label: "Standard S3", value: "https://demo-bucket.s3.amazonaws.com/" },
    { label: "Region Specific", value: "https://my-data.s3.us-west-2.amazonaws.com/" },
    { label: "Static Hosting", value: "http://website.s3-website-us-east-1.amazonaws.com" }
  ];

  return (
    <div className="scan-card d-flex justify-content-center align-items-center">
      <div className="card shadow-sm border-1 rounded-4">
        <div className="d-flex flex-row align-items-center h-100">

          {/* 🔹 Left: Form Section */}
          <div className="scan-form-section p-3 w-50">
            <form
              onSubmit={handleSubmit}
              className="d-flex flex-column justify-content-center align-items-center"
            >
              <div className="fs-1 fw-bold text-center heading mb-3">
                Scan S3 URL
              </div>

              <div
                className="fs-6 text-center fw-semibold mb-5 subheading text-muted"
                style={{ width: 280 }}>
                Enter the s3 bucket url below to scan for potential vulnerabilities.
              </div>

              <div
                className="mb-2 w-100 input-group rounded-2"
                style={{ height: 43 }}
              >
                <input
                  type="text"
                  placeholder="Enter S3 URL"
                  className="p-2 text-center form-control"
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={urlScanLoading || urlDashboardView}
                />
              </div>

              <div style={{ width: 260 }}>
                {error && (
                  <div
                    className="error-hover text-danger text-center fw-semibold mb-2"
                    onClick={error.card ? toggleUpgradeCard : undefined}
                    style={{ cursor: error.card ? "pointer" : "default" }}
                  >
                    {error.message}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-success w-100 py-2 fw-semibold fs-6"
                disabled={urlScanLoading || urlDashboardView || !url}
                style={{ height: 46 }}
              >
                Submit
              </button>
            </form>
          </div>

          <div className="scan-video-section h-100 w-50 p-4 bg-light d-flex flex-column border-start overflow-auto">
            {/* Documentation Header */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-book-half text-primary fs-5"></i>
                <span className="fw-bold text-dark tracking-tight">URL DOCUMENTATION</span>
              </div>
              <p className="text-muted" style={{ fontSize: '13px', lineHeight: '1.6' }}>
                An Amazon S3 bucket URL is the public or private endpoint used to access your stored data.
                To scan correctly, ensure your URL follows one of the AWS standard naming conventions.
              </p>
            </div>

            {/* Header */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-shield-lock-fill text-primary fs-5"></i>
                <span className="fw-bold text-dark tracking-tight">S3 DEPLOYMENT GUIDE</span>
              </div>
              <p className="text-muted" style={{ fontSize: '12.5px', lineHeight: '1.5' }}>
                Follow these steps to create your bucket and generate a compatible URL for security scanning.
              </p>
            </div>

            {/* Full Process Steps */}
            <div className="d-flex flex-column gap-4 mb-4">

              {/* Step 1: Creation */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 1</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Bucket Creation</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Log in to <strong>AWS Console</strong>, search for <strong>S3</strong>, and click <strong>Create bucket</strong>.</>,
                    <>Provide a globally unique name and choose your target region.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 2: Access & Policy */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 2</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Set Permissions</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>In the <strong>Permissions</strong> tab, ensure <strong>Block all public access</strong> is configured based on your security needs.</>,
                    <>Add a <strong>Bucket Policy</strong> if restricted access is required for scanning.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 3: URL Copy */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 3</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Retrieve Endpoint</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Open your bucket, navigate to the <strong>Properties</strong> tab, and scroll to <strong>Bucket overview</strong>.</>,
                    <>Copy the <strong>Amazon Resource Name (ARN)</strong> or the regional <strong>Endpoint URL</strong>.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Supported Examples Footer */}
            <div className="mt-auto pt-3 border-top">
              <div className="mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-info-circle-fill text-success"></i>
                <span className="fw-bold small text-secondary">VALID URL FORMATS</span>
              </div>

              <div className="d-flex flex-column gap-2">
                {urlExamples.map((ex, i) => (
                  <div key={i} className="p-2 rounded bg-white border border-light-subtle shadow-sm">
                    <div className="text-primary fw-bold mb-1" style={{ fontSize: '9px', textTransform: 'uppercase' }}>{ex.label}</div>
                    <code className="text-dark-emphasis d-block text-truncate p-1 bg-light rounded" style={{ fontSize: '11px' }}>
                      {ex.value}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanCard;
